/**
 * FRONT-2512: the "What's new" note shown after a release. This file is the
 * whole authoring surface: edit the text here, merge before the release PR,
 * and every tenant sees it the moment the deploy lands. A slim bar under the
 * app header opens the note in a centred window (the chat window's family).
 *
 * - A new note needs a NEW `id` (`YYYY-MM-DD-vX.Y.Z`). The id is what a
 *   person's "closed it" is remembered against, so reusing one keeps it
 *   hidden for everyone who closed the old note.
 * - Most releases carry no note: set `CURRENT_RELEASE_NOTE` to `null` and
 *   nothing renders.
 * - Every line says WHAT changed and WHY it matters to the reader, in both
 *   English and German. The "why" is the part people remember; never ship a
 *   line without one.
 * - The `de` strings are user-facing copy, a deliberate exception to the
 *   repo's English-only rule for working text; keep them German.
 * - Name every button, page and setting by its English label, in the
 *   German text too: the app itself is English only, so a translated name
 *   ("Richtlinien") points at a place nobody can find.
 * - `adminOnly` tags a line "Admins" for things only admins can open.
 * - `link` turns a page name inside `what` into a link to that page. The
 *   name is the English label, so it is the same text in both languages.
 * - `flag` hides a line from anyone whose workspace does not have that
 *   feature flag on, so nobody reads about a thing they cannot open. The
 *   key maps to the real flag in `RELEASE_NOTE_FLAGS`
 *   (`whats-new-banner.tsx`); add a key there to use a new one. Anything
 *   behind a flag that is off everywhere stays out of the note entirely.
 *
 * No imports on purpose: the e2e setup reads the id from this file to close
 * the note on the test accounts before the journeys run.
 */

/** Keys, not flag names: this file stays import-free (see above), and the
 *  registry constants live behind `RELEASE_NOTE_FLAGS`. */
export type ReleaseNoteFlag =
  | 'sketches'
  | 'channelSlack'
  | 'brandKit'
  | 'modelPicker'
  | 'canvas'
  | 'slideDecks'
  | 'computeBlocks'
  | 'codingSpend'
  | 'orgMcpServers'
  | 'websiteImport';

export type ReleaseNoteLang = 'en' | 'de';

/** One piece of text in every language the note is written in. */
export type Localized = Record<ReleaseNoteLang, string>;

export interface ReleaseNoteItem {
  /** What is new, from the reader's side: "In Projects you can now ...". */
  what: Localized;
  /** Why it matters: "This lets you ...". */
  why: Localized;
  adminOnly?: boolean;
  /** Links `label` (text that appears in `what`, in every language) to an in-app path. */
  link?: { label: string; href: string };
  /** Show this line only where this feature's flag is on. */
  flag?: ReleaseNoteFlag;
}

export interface ReleaseNoteSection {
  title: Localized;
  items: ReleaseNoteItem[];
}

export interface ReleaseNote {
  /** Must match `RELEASE_NOTE_ID_PATTERN` in `src/admin/ui-prefs-store.ts`. */
  id: string;
  /** Shown above the title in the window, e.g. "0.24". */
  version: string;
  /** The bar's one line, and the window's title. */
  headline: Localized;
  /** One or two sentences under the title. */
  intro: Localized;
  sections: ReleaseNoteSection[];
}

export const CURRENT_RELEASE_NOTE: ReleaseNote | null = {
  id: '2026-10-22-v0.27.0',
  version: '0.27',
  headline: {
    en: 'EDDY 0.27: edit any page of an App from chat, and more.',
    de: 'EDDY 0.27: jede Seite einer App im Chat bearbeiten und mehr.',
  },
  intro: {
    en: 'What is new, and what each change does for you.',
    de: 'Was neu ist, und was dir jede Änderung bringt.',
  },
  sections: [
    {
      title: { en: 'Apps', de: 'Apps' },
      items: [
        {
          what: {
            en: 'On the Apps page, "Import a website" copies a site you own into a private draft App. EDDY scans the site first, you choose the pages and see how long it will take, and then you watch it arrive page by page.',
            de: 'Auf der Apps-Seite kopiert „Import a website“ eine Website, die dir gehört, in eine private App im Entwurf. EDDY scannt die Seite zuerst, du wählst die Seiten aus und siehst, wie lange es dauert, und dann kommt sie Seite für Seite an.',
          },
          why: {
            en: 'You start from your real site instead of rebuilding it, and nothing goes live until you publish it.',
            de: 'Du startest mit deiner echten Website, statt sie nachzubauen, und nichts geht live, bevor du es veröffentlichst.',
          },
          // internal until a legal and compliance check clears the import for customers
          flag: 'websiteImport',
        },
        {
          what: {
            en: 'Ask EDDY in chat to change any page of an App, not only the home page, including very large pages.',
            de: 'Bitte EDDY im Chat, jede Seite einer App zu ändern, nicht nur die Startseite, auch sehr große Seiten.',
          },
          why: {
            en: '"Change the headline on our About page" now just works.',
            de: '„Ändere die Überschrift auf unserer About-Seite“ funktioniert jetzt einfach.',
          },
        },
        {
          what: {
            en: 'The Data store card on an App’s edit page shows how full its storage is. Before it fills up, the Apps list and the bell warn you, and you can ask EDDY in chat to clear an old collection in one step. An App now holds up to 50,000 entries instead of 10,000.',
            de: 'Die Karte Data store auf der Bearbeitungsseite einer App zeigt, wie voll ihr Speicher ist. Bevor er voll ist, warnen dich die Apps-Liste und die Glocke, und du kannst EDDY im Chat bitten, eine alte Sammlung in einem Schritt zu leeren. Eine App fasst jetzt bis zu 50.000 Einträge statt 10.000.',
          },
          why: {
            en: 'You see a full App coming and make room before it stops saving.',
            de: 'Du siehst kommen, dass eine App voll wird, und schaffst Platz, bevor sie nichts mehr speichert.',
          },
        },
        {
          flag: 'orgMcpServers',
          what: {
            en: 'An App’s page can now call the read-only tools of your organization’s shared MCP servers directly, as the signed-in visitor.',
            de: 'Die Seite einer App kann jetzt die Nur-Lese-Tools der geteilten MCP-Server deiner Organisation direkt aufrufen, im Namen der angemeldeten Besucher.',
          },
          why: {
            en: 'Data from those tools appears in seconds, without waiting for an agent run.',
            de: 'Daten aus diesen Tools erscheinen in Sekunden, ohne auf einen Agentenlauf zu warten.',
          },
        },
        {
          what: {
            en: 'A workflow step can save the previous step’s result into one of your Apps as a real entry.',
            de: 'Ein Workflow-Schritt kann das Ergebnis des vorherigen Schritts als echten Eintrag in einer deiner Apps speichern.',
          },
          why: {
            en: 'Workflow results go straight into the App that shows them.',
            de: 'Workflow-Ergebnisse landen direkt in der App, die sie anzeigt.',
          },
        },
        {
          adminOnly: true,
          what: {
            en: 'An App without an owner can no longer be changed by everyone in the organization. On the Apps page, an admin can assign it an owner.',
            de: 'Eine App ohne Besitzer kann nicht mehr von allen in der Organisation geändert werden. Auf der Apps-Seite kann ein Admin ihr einen Besitzer zuweisen.',
          },
          why: {
            en: 'Nobody deletes or unpublishes an App by accident just because no owner was recorded.',
            de: 'Niemand löscht oder versteckt eine App aus Versehen, nur weil kein Besitzer eingetragen war.',
          },
        },
      ],
    },
    {
      title: { en: 'Builder', de: 'Builder' },
      items: [
        {
          what: {
            en: 'The Builder now takes up to 250 steps per message instead of 80. If it still reaches the limit, it pauses with a Continue button instead of failing.',
            de: 'Der Builder macht jetzt bis zu 250 Schritte pro Nachricht statt 80. Erreicht er die Grenze trotzdem, pausiert er mit einem Continue-Button, statt abzubrechen.',
          },
          why: {
            en: 'Larger changes to an App finish, and nothing is lost when one needs more steps.',
            de: 'Größere Änderungen an einer App werden fertig, und nichts geht verloren, wenn eine mehr Schritte braucht.',
          },
        },
        {
          what: {
            en: 'While the Builder works, it shows how long ago its last step was. After 10 quiet minutes it says the build may be stuck and offers Stop. When every build slot is busy, or a tool connection is missing, it says so in plain words.',
            de: 'Während der Builder arbeitet, zeigt er, wie lange sein letzter Schritt her ist. Nach 10 stillen Minuten sagt er, dass der Build hängen könnte, und bietet Stop an. Sind alle Build-Plätze belegt oder fehlt eine Tool-Verbindung, sagt er das in klaren Worten.',
          },
          why: {
            en: 'You know whether to keep waiting, and a missing connection no longer looks like the Builder refusing.',
            de: 'Du weißt, ob sich Warten lohnt, und eine fehlende Verbindung sieht nicht mehr so aus, als würde der Builder sich weigern.',
          },
        },
      ],
    },
    {
      title: { en: 'Chat', de: 'Chat' },
      items: [
        {
          what: {
            en: 'EDDY now knows which features your workspace has, and talks only about those.',
            de: 'EDDY weiß jetzt, welche Funktionen dein Workspace hat, und spricht nur über diese.',
          },
          why: {
            en: 'When you ask what EDDY can do, the answer matches what you see.',
            de: 'Wenn du fragst, was EDDY kann, passt die Antwort zu dem, was du siehst.',
          },
        },
        {
          what: {
            en: 'Ask EDDY in chat what a workflow run did, and it reads the run step by step.',
            de: 'Frag EDDY im Chat, was ein Workflow-Lauf gemacht hat, und EDDY liest den Lauf Schritt für Schritt.',
          },
          why: {
            en: 'After a failed run you learn which steps finished and what was written.',
            de: 'Nach einem fehlgeschlagenen Lauf erfährst du, welche Schritte fertig wurden und was geschrieben wurde.',
          },
        },
        {
          what: {
            en: 'Stop in chat now also stops a tool call that is still running on an MCP server.',
            de: 'Stop im Chat stoppt jetzt auch einen Tool-Aufruf, der noch auf einem MCP-Server läuft.',
          },
          why: {
            en: 'Work you stopped does not carry on in the background.',
            de: 'Arbeit, die du gestoppt hast, läuft nicht im Hintergrund weiter.',
          },
        },
        {
          flag: 'orgMcpServers',
          link: { label: 'System Settings > Connections', href: '/system-settings?section=connections' },
          what: {
            en: 'When your sign-in to a shared MCP server expires, System Settings > Connections shows Reconnect instead of an error.',
            de: 'Wenn deine Anmeldung bei einem geteilten MCP-Server abläuft, zeigt System Settings > Connections Reconnect statt eines Fehlers.',
          },
          why: {
            en: 'You sign in again with one click.',
            de: 'Du meldest dich mit einem Klick wieder an.',
          },
        },
      ],
    },
    {
      title: { en: 'Reliability & security', de: 'Zuverlässigkeit & Sicherheit' },
      items: [
        {
          what: {
            en: 'Custom tools now belong to the person who made them: nobody else sees or runs them, and a custom tool can no longer replace a built-in one.',
            de: 'Custom Tools gehören jetzt der Person, die sie erstellt hat: Niemand sonst sieht oder startet sie, und ein Custom Tool kann kein eingebautes Tool mehr ersetzen.',
          },
          why: {
            en: 'Your tools stay yours, and the built-in tools work the same for everyone.',
            de: 'Deine Tools bleiben deine, und die eingebauten Tools funktionieren für alle gleich.',
          },
        },
        {
          what: {
            en: 'App previews on the Apps page and in chat no longer play sound.',
            de: 'App-Vorschauen auf der Apps-Seite und im Chat spielen keinen Ton mehr ab.',
          },
          why: {
            en: 'An App that plays a sound does so only when you open it.',
            de: 'Eine App, die einen Ton abspielt, tut das nur, wenn du sie öffnest.',
          },
        },
        {
          what: {
            en: 'The activity feed labels Today, Yesterday and weekdays by your own calendar.',
            de: 'Der Aktivitäts-Feed beschriftet Today, Yesterday und Wochentage nach deinem eigenen Kalender.',
          },
          why: {
            en: 'The labels are right just after midnight, wherever you are.',
            de: 'Die Beschriftungen stimmen auch kurz nach Mitternacht, egal wo du bist.',
          },
        },
      ],
    },
  ],
};
